import React from 'react';
import {
  Box,
  LinearProgress,
  Typography,
  Paper,
  Chip,
  Stack,
} from '@mui/material';
import { CheckCircle, Warning, Error } from '@mui/icons-material';

/**
 * Usage Meter Component
 * Displays page usage with progress bar and status
 */
const UsageMeter = ({ subscription, compact = false }) => {
  if (!subscription) {
    return null;
  }

  const {
    pagesUsed = 0,
    maxPages,
    pagesRemaining,
    usagePercent = 0,
    status,
    isActive,
  } = subscription;

  // Format pages used to show as decimal with 2 places
  const formatPages = (pages) => {
    if (typeof pages === 'number') {
      return pages.toFixed(2);
    }
    return parseFloat(pages || 0).toFixed(2);
  };

  const pagesUsedFormatted = formatPages(pagesUsed);
  const pagesRemainingFormatted = formatPages(pagesRemaining);

  // Determine status color and icon
  const getStatusInfo = () => {
    if (status === 'whitelisted') {
      return {
        color: 'success',
        icon: <CheckCircle />,
        label: 'Unlimited',
        description: 'You have unlimited access',
      };
    }

    if (!isActive) {
      return {
        color: 'error',
        icon: <Error />,
        label: 'Expired',
        description: 'Subscription expired - Please renew',
      };
    }

    if (usagePercent >= 95) {
      return {
        color: 'error',
        icon: <Error />,
        label: 'Limit Reached',
        description: 'You have used 95%+ of your pages',
      };
    }

    if (usagePercent >= 80) {
      return {
        color: 'warning',
        icon: <Warning />,
        label: 'Low Pages',
        description: 'You have used 80%+ of your pages',
      };
    }

    return {
      color: 'success',
      icon: <CheckCircle />,
      label: 'Active',
      description: 'Subscription is active',
    };
  };

  const statusInfo = getStatusInfo();
  const isUnlimited = status === 'whitelisted' || maxPages === 'Unlimited';

  // Compact version (for dashboard/header)
  if (compact) {
    return (
      <Box sx={{ minWidth: 200 }}>
        <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5 }}>
          <Typography variant="body2" color="text.secondary">
            Pages Used:
          </Typography>
          <Typography variant="body2" fontWeight="bold">
            {isUnlimited ? 'Unlimited' : `${pagesUsedFormatted}/${maxPages}`}
          </Typography>
          {!isUnlimited && (
            <Chip
              label={`${usagePercent}%`}
              size="small"
              color={statusInfo.color}
            />
          )}
        </Stack>
        {!isUnlimited && (
          <LinearProgress
            variant="determinate"
            value={Math.min(usagePercent, 100)}
            color={statusInfo.color}
            sx={{ height: 6, borderRadius: 1 }}
          />
        )}
      </Box>
    );
  }

  // Full version (for subscription page)
  return (
    <Paper elevation={2} sx={{ p: 3 }}>
      <Stack spacing={2}>
        {/* Header */}
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Typography variant="h6">Page Usage</Typography>
          <Chip
            icon={statusInfo.icon}
            label={statusInfo.label}
            color={statusInfo.color}
            size="small"
          />
        </Stack>

        {/* Usage Stats */}
        <Box>
          {isUnlimited ? (
            <Stack spacing={1}>
              <Typography variant="h3" color="success.main" fontWeight="bold">
                ∞
              </Typography>
              <Typography variant="body1" color="text.secondary">
                Unlimited Pages
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {statusInfo.description}
              </Typography>
            </Stack>
          ) : (
            <Stack spacing={2}>
              {/* Page Count */}
              <Stack direction="row" spacing={2} alignItems="baseline">
                <Typography variant="h3" fontWeight="bold" color="primary.main">
                  {pagesUsedFormatted}
                </Typography>
                <Typography variant="h6" color="text.secondary">
                  / {maxPages} pages
                </Typography>
              </Stack>

              {/* Progress Bar */}
              <Box>
                <LinearProgress
                  variant="determinate"
                  value={Math.min(usagePercent, 100)}
                  color={statusInfo.color}
                  sx={{ height: 12, borderRadius: 2 }}
                />
                <Stack
                  direction="row"
                  justifyContent="space-between"
                  sx={{ mt: 1 }}
                >
                  <Typography variant="caption" color="text.secondary">
                    {pagesUsedFormatted} used
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {pagesRemainingFormatted} remaining
                  </Typography>
                </Stack>
              </Box>

              {/* Warning Message */}
              {usagePercent >= 80 && (
                <Box
                  sx={{
                    p: 1.5,
                    bgcolor: statusInfo.color === 'error' ? 'error.light' : 'warning.light',
                    borderRadius: 1,
                  }}
                >
                  <Typography variant="body2" color={`${statusInfo.color}.dark`}>
                    {statusInfo.description}
                  </Typography>
                </Box>
              )}
            </Stack>
          )}
        </Box>
      </Stack>
    </Paper>
  );
};

export default UsageMeter;

